import { reduce, sortBy, partition } from 'lodash';
import styled from '@emotion/styled';
import { last } from 'lodash';
import { ThemeProps } from '@theme/base';
import { Page } from '@model/common';
import { GenericLinkList, MAX_LINK_COUNT } from './GenericLinkList';
import { HelpSubCategory } from '@model/iceberg/help-centre';
import { mixins } from '@styles/mixins';
import { getPathFromName } from '@util/path';
import { TestId } from '@components/test-ids';
import { Grid } from '@mui/material';

const COLUMN_COUNT = 3;

/* ***************** *
 *       Types       *
 * ***************** */

export interface HelpCentreLinkListGroupProps {
  linkGroup: Array<HelpSubCategory>;
  linkGroupTitle?: string;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const LinkListItemContainer: any = styled.div(({ theme }: ThemeProps) => ({
  ...mixins(theme).card,
  width: '100%',
  marginBottom: theme.custom.spacing.large,
  transform: 'translateZ(0px)'
}));

const GridContainer = styled(Grid)({
  marginTop: 0,
  marginBottom: 0
});

const GridItem = styled(Grid)({
  ['&.MuiGrid-item']: {
    paddingTop: 0,
    paddingBottom: 0
  }
});

/* ************************* *
 *       LinkListGroup       *
 * ************************* */

export const HelpCentreLinkListGroup = ({ linkGroup, linkGroupTitle }: HelpCentreLinkListGroupProps) => {
  const getLinkGroup = (link, sortLink = false) => {
    return link.map((group: HelpSubCategory) => {
      const { articles, title: groupTitle } = group;
      const links = articles.map(({ title }) => {
        return {
          label: title,
          linkUrl: {
            as: `${Page.HELP_CENTRE}/${getPathFromName(linkGroupTitle || groupTitle)}/${getPathFromName(title)}`,
            href: `${Page.HELP_CENTRE}/[category]/[article]`
          }
        };
      });

      return {
        id: group.id,
        title: group.title,
        links: sortLink ? sortBy(links, ['label']) : links
      };
    });
  };

  const renderLinkListGroup = (linkGroup) => {
    const linkIDs = [30];
    const [filteredLinkGroup, unfilteredLinkGroup] = partition(linkGroup, ({ id }) => linkIDs.includes(id));
    const filteredLink = getLinkGroup(filteredLinkGroup, true);
    const unfilteredLink = getLinkGroup(unfilteredLinkGroup);

    const sortedLinkGroups = [...filteredLink, ...unfilteredLink];

    return sortedLinkGroups.map((group) => {
      const { id, title, links } = group;
      return (
        <LinkListItemContainer key={id}>
          <GenericLinkList title={title} links={links} />
        </LinkListItemContainer>
      );
    });
  };

  const sortedSubCategories = linkGroup.slice().sort((a, b) => b.articles.length - a.articles.length);
  const articleCount = reduce(
    sortedSubCategories,
    (total, { articles }) => total + Math.min(articles.length, MAX_LINK_COUNT),
    0
  );
  const articlesPerColumn = Math.floor(articleCount / COLUMN_COUNT);
  const [subCategoriesInColumns] = reduce(
    sortedSubCategories,
    ([subCateoryGroups, runningCount], subCategory) => {
      let newRunningCount = runningCount + Math.min(subCategory.articles.length, MAX_LINK_COUNT);
      if (!subCateoryGroups.length) {
        subCateoryGroups.push([subCategory]);
      } else {
        const currentGroup = last(subCateoryGroups) || [];
        subCateoryGroups[subCateoryGroups.length - 1] = currentGroup.concat(subCategory);
      }
      if (newRunningCount >= articlesPerColumn) {
        newRunningCount = 0;
        subCateoryGroups.push([]);
      }
      return [subCateoryGroups, newRunningCount];
    },
    [[] as Array<Array<HelpSubCategory>>, 0]
  );

  return (
    <>
      <GridContainer container spacing={4} data-testid={TestId.helpCentre.linkListGroup.main}>
        {subCategoriesInColumns.map(
          (subCategoryGroup) =>
            subCategoryGroup.length > 0 && (
              <GridItem item xs={12} sm={12} md={4} key={subCategoryGroup[0].title}>
                {renderLinkListGroup(subCategoryGroup)}
              </GridItem>
            )
        )}
      </GridContainer>
    </>
  );
};
